import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import noop from 'lodash/noop';

import useLocalStorage from '../../../services/hooks/useLocalStorage';

// User-level UI preferences, persisted in localStorage. Add new preferences here as the
// need arises (e.g. preferred language, date formats).
type UserPreferences = Readonly<{
  hideInactiveProjects: boolean;
  notifyOnProgressionChange: boolean;
}>;

const defaultPreferences: UserPreferences = {
  hideInactiveProjects: false,
  notifyOnProgressionChange: true,
};

type ContextValue = Readonly<{
  preferences: UserPreferences;
  setPreferences: (patch: Partial<UserPreferences>) => void;
}>;

const context = createContext<ContextValue>({
  preferences: defaultPreferences,
  setPreferences: noop,
});

type Props = Readonly<{
  children: ReactNode;
}>;

const UserPreferencesProvider = ({ children }: Props): ReactElement => {
  const [storedPreferences, setStoredPreferences] = useLocalStorage<UserPreferences>(
    'userPreferences', defaultPreferences,
  );

  const value: ContextValue = {
    // Merge over defaults so preferences added in the future still resolve for existing
    // users whose stored blob predates them.
    preferences: { ...defaultPreferences, ...storedPreferences },
    setPreferences: (patch) => setStoredPreferences((previous) => ({ ...previous, ...patch })),
  };

  return (
    <context.Provider value={value}>
      {children}
    </context.Provider>
  );
};

export const useUserPreferences = (): ContextValue => useContext(context);

export default UserPreferencesProvider;
